import type { PriceToleranceRule, SupplierPriceUpdate } from "../domain/types";
import {
    TR008_RULE_ID,
    getTableTextRuleIntegrationAdapter,
} from "../appglueIntegration/policies/tableTextRulePolicyRegistry";
import {
    createRuleIntegrationConfig,
    evaluateRuleIntegration,
} from "../appglueIntegration/ruleIntegrationTypes";

export type PriceToleranceOutcome = "auto_approved" | "flagged" | "held";

export type PriceToleranceSummary = {
    autoApproved: number;
    flagged: number;
    held: number;
};

export type PriceToleranceChange = {
    update: SupplierPriceUpdate;
    before: PriceToleranceOutcome;
    after: PriceToleranceOutcome;
};

export const emptyPriceToleranceSummary = (): PriceToleranceSummary => ({
    autoApproved: 0,
    flagged: 0,
    held: 0,
});

export const priceToleranceOutcomeLabel = (outcome: PriceToleranceOutcome): string => {
    if (outcome === "auto_approved") return "Auto-approved";
    if (outcome === "flagged") return "Flagged";
    return "Held";
};

export const buildPriceToleranceRuleMap = (rules: PriceToleranceRule[]): Map<string, PriceToleranceRule> =>
    new Map(rules.map((rule) => [rule.manufacturerId, rule] as const));

const priceToleranceAdapter = getTableTextRuleIntegrationAdapter(TR008_RULE_ID)!;

const isPriceToleranceOutcome = (value: unknown): value is PriceToleranceOutcome =>
    value === "auto_approved" || value === "flagged" || value === "held";

const evaluateConfiguredPriceToleranceOutcome = (
    config: ReturnType<typeof createRuleIntegrationConfig>,
    update: SupplierPriceUpdate,
): PriceToleranceOutcome => {
    const result = evaluateRuleIntegration(priceToleranceAdapter, config, update);
    const outcome = (result.evidence as { outcome?: unknown } | undefined)?.outcome;
    if (isPriceToleranceOutcome(outcome)) return outcome;
    if (result.status === "pass") return "auto_approved";
    if (result.status === "flagged") return "flagged";
    return "held";
};

export const evaluatePriceToleranceOutcome = (
    update: SupplierPriceUpdate,
    rulesByManufacturer: Map<string, PriceToleranceRule>,
): PriceToleranceOutcome =>
    evaluateConfiguredPriceToleranceOutcome(
        createRuleIntegrationConfig(priceToleranceAdapter, {
            priceToleranceRules: Array.from(rulesByManufacturer.values()),
        }),
        update,
    );

export const summarizePriceToleranceImpact = (
    updates: SupplierPriceUpdate[],
    rules: PriceToleranceRule[],
): PriceToleranceSummary => {
    const config = createRuleIntegrationConfig(priceToleranceAdapter, {
        priceToleranceRules: rules,
    });

    return updates.reduce<PriceToleranceSummary>((summary, update) => {
        const outcome = evaluateConfiguredPriceToleranceOutcome(config, update);
        if (outcome === "auto_approved") summary.autoApproved += 1;
        if (outcome === "flagged") summary.flagged += 1;
        if (outcome === "held") summary.held += 1;
        return summary;
    }, emptyPriceToleranceSummary());
};

export const comparePriceToleranceImpact = (
    updates: SupplierPriceUpdate[],
    baselineRules: PriceToleranceRule[],
    candidateRules: PriceToleranceRule[],
    limit = 8,
): PriceToleranceChange[] => {
    const baseline = createRuleIntegrationConfig(priceToleranceAdapter, {
        priceToleranceRules: baselineRules,
    });
    const candidate = createRuleIntegrationConfig(priceToleranceAdapter, {
        priceToleranceRules: candidateRules,
    });
    const changes: PriceToleranceChange[] = [];

    for (const update of updates) {
        const before = evaluateConfiguredPriceToleranceOutcome(baseline, update);
        const after = evaluateConfiguredPriceToleranceOutcome(candidate, update);

        if (before !== after) {
            changes.push({ update, before, after });
            if (changes.length >= limit) return changes;
        }
    }

    return changes;
};
